import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useActiveProjectId, useNotify } from '../../hooks';
import { useApiClient } from '../../hooks/useRestAPIHooks';

const POLL_INTERVAL_MS = 3000;

const IN_FLIGHT_STATUSES = ['scheduled', 'pending', 'processing', 'waiting'];

const messages = defineMessages({
  finished: {
    id: 'phc-web-toolkit.memberExtractions.reextract.finished',
    defaultMessage: 'Re-extraction finished.',
  },
  failed: {
    id: 'phc-web-toolkit.memberExtractions.reextract.failed',
    defaultMessage: 'Re-extraction failed.',
  },
});

type ExtractionTask = {
  task_id?: string;
  status?: string;
  source_resource_type?: string | null;
  source_resource_id?: string | null;
  source_document_reference_id?: string | null;
};

const isInFlight = (status?: string) =>
  !status || IN_FLIGHT_STATUSES.includes(status);

const taskSourceReference = (task: ExtractionTask) => {
  if (task.source_resource_type && task.source_resource_id) {
    return `${task.source_resource_type}/${task.source_resource_id}`;
  }
  return task.source_document_reference_id
    ? `DocumentReference/${task.source_document_reference_id}`
    : undefined;
};

const NO_TASKS: Record<string, string> = {};

type TrackedTasks = {
  member: string;
  tasks: Record<string, string>;
};

export type MemberExtractionReextractions = {
  isRunning: (sourceReference: string) => boolean;
  track: (sourceReference: string, taskId: string) => void;
};

/**
 * Tracks re-extraction runs for a member by source document, so a run keeps
 * being followed while the reviewer moves between documents.
 */
export const useMemberExtractionReextractions = (
  patientId: string | null | undefined,
  onCompleted: (sourceReference: string) => void,
): MemberExtractionReextractions => {
  const { formatMessage } = useIntl();
  const apiClient = useApiClient();
  const projectId = useActiveProjectId();
  const [notify] = useNotify();
  const member = patientId && projectId ? `${projectId}/${patientId}` : '';
  const [tracked, setTracked] = React.useState<TrackedTasks>({
    member,
    tasks: NO_TASKS,
  });
  const tasks = tracked.member === member ? tracked.tasks : NO_TASKS;
  const memberRef = React.useRef(member);
  const onCompletedRef = React.useRef(onCompleted);
  const notifyRef = React.useRef(notify);
  const formatMessageRef = React.useRef(formatMessage);
  memberRef.current = member;
  onCompletedRef.current = onCompleted;
  notifyRef.current = notify;
  formatMessageRef.current = formatMessage;

  // Runs belong to the member they were found for; a late update for a member
  // no longer shown is dropped.
  const setTasks = React.useCallback(
    (
      forMember: string,
      update: (current: Record<string, string>) => Record<string, string>,
    ) => {
      if (forMember !== memberRef.current) {
        return;
      }
      setTracked(current => ({
        member: forMember,
        tasks: update(current.member === forMember ? current.tasks : NO_TASKS),
      }));
    },
    [],
  );

  // Runs started elsewhere, or before this page was loaded, are still in flight.
  React.useEffect(() => {
    if (!patientId || !projectId) {
      return undefined;
    }
    let cancelled = false;
    apiClient
      .request('GET /v1/agents-v2/document-extraction-v2/tasks', {
        subject_id: patientId,
        project_id: projectId,
        limit: 25,
      })
      .then(response => {
        if (cancelled) {
          return;
        }
        const running: Record<string, string> = {};
        (response?.data?.tasks ?? []).forEach((task: ExtractionTask) => {
          const sourceReference = taskSourceReference(task);
          if (
            sourceReference &&
            task.task_id &&
            isInFlight(task.status) &&
            !running[sourceReference]
          ) {
            running[sourceReference] = task.task_id;
          }
        });
        if (Object.keys(running).length > 0) {
          setTasks(member, current => ({ ...running, ...current }));
        }
      })
      .catch(() => {
        // Without the task list, a run in flight is found when re-extract gets a 409.
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient, member, patientId, projectId, setTasks]);

  React.useEffect(() => {
    const entries = Object.entries(tasks);
    if (entries.length === 0) {
      return undefined;
    }
    const polledMember = member;
    let cancelled = false;
    let polling = false;

    const poll = () => {
      if (polling) {
        return;
      }
      polling = true;
      Promise.all(
        entries.map(([sourceReference, taskId]) =>
          apiClient
            .request(
              'GET /v1/agents-v2/document-extraction-v2/invocations/:task_id',
              { task_id: taskId },
            )
            .then(
              response => ({
                sourceReference,
                taskId,
                status: (response as { data?: { status?: string } }).data
                  ?.status,
              }),
              // A missed status check should not end the run. The next poll retries.
              () => undefined,
            ),
        ),
      )
        .then(results => {
          if (cancelled || memberRef.current !== polledMember) {
            return;
          }
          const finished = results.filter(
            (result): result is NonNullable<typeof result> =>
              Boolean(result && !isInFlight(result.status)),
          );
          if (finished.length === 0) {
            return;
          }
          setTasks(polledMember, current => {
            const next = { ...current };
            finished.forEach(result => {
              if (next[result.sourceReference] === result.taskId) {
                delete next[result.sourceReference];
              }
            });
            return next;
          });
          finished.forEach(result => {
            if (result.status === 'completed') {
              onCompletedRef.current(result.sourceReference);
              notifyRef.current(
                formatMessageRef.current(messages.finished),
                'success',
              );
            } else {
              notifyRef.current(
                formatMessageRef.current(messages.failed),
                'error',
              );
            }
          });
        })
        .finally(() => {
          polling = false;
        });
    };

    poll();
    const interval = window.setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [apiClient, member, setTasks, tasks]);

  const isRunning = React.useCallback(
    (sourceReference: string) => Boolean(tasks[sourceReference]),
    [tasks],
  );
  const track = React.useCallback(
    (sourceReference: string, taskId: string) =>
      setTasks(member, current => ({ ...current, [sourceReference]: taskId })),
    [member, setTasks],
  );

  return { isRunning, track };
};
